import { createAsyncThunk } from '@reduxjs/toolkit';
import api, { handleApiError } from 'state/api';

export const getInventoriesApi = createAsyncThunk(
  'inventories/getInventoriesApi',
  async ({ page = 1, limit = 10, search, supplier, status }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      if (search) params.append('search', search);
      if (supplier && supplier !== 'all') params.append('supplier', supplier);
      if (status) params.append('status', status);

      const response = await api.get(`inventory-management?${params.toString()}`);
      return response.data.success ? response.data.data : response.data;
    } catch (error) {
      return handleApiError('getInventoriesApi', error, rejectWithValue);
    }
  },
);

export const getInventoriesOverviewApi = createAsyncThunk(
  'inventories/getInventoriesOverviewApi',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('inventory-management/overview');
      return response.data.success ? response.data.data : response.data;
    } catch (error) {
      return handleApiError('getInventoriesOverviewApi', error, rejectWithValue);
    }
  },
);

export const deleteInventoryApi = createAsyncThunk(
  'inventories/deleteInventoryApi',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.delete(`inventory-management/${id}`);
      return response.data;
    } catch (error) {
      return handleApiError('deleteInventoryApi', error, rejectWithValue);
    }
  },
);

export const downloadInventoryApi = createAsyncThunk(
  'inventories/downloadInventoryApi',
  async ({ id, fileName }, { rejectWithValue }) => {
    try {
      const response = await api.get(`inventory-management/download/${id}`, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return { message: 'Download started successfully' };
    } catch (error) {
      return handleApiError('downloadInventoryApi', error, rejectWithValue);
    }
  },
);

export const uploadInventoryApi = createAsyncThunk(
  'inventories/uploadInventoryApi',
  async ({ file, supplierId, supplierName }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('supplierId', supplierId);
      formData.append('supplierName', supplierName);

      const response = await api.post('inventory-management/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      return handleApiError('uploadInventoryApi', error, rejectWithValue);
    }
  },
);

export const deleteInventoriesByFilterApi = createAsyncThunk(
  'inventories/deleteInventoriesByFilterApi',
  async ({ startDate, endDate }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({
        startDate,
        endDate,
      });

      const response = await api.delete(`inventory-management?${params.toString()}`);
      return response.data;
    } catch (error) {
      return handleApiError('deleteInventoriesByFilterApi', error, rejectWithValue);
    }
  },
);

export const searchInventoryApi = createAsyncThunk(
  'inventories/searchInventoryApi',
  async (
    {
      searchTerm,
      searchField = 'all',
      supplierIds,
      page = 1,
      limit = 20,
      includeImages = false,
    },
    { rejectWithValue },
  ) => {
    try {
      const params = new URLSearchParams({
        searchTerm,
        searchField,
        page: page.toString(),
        limit: limit.toString(),
      });
      if (includeImages) {
        params.append('includeImages', 'true');
      }

      if (supplierIds) {
        if (Array.isArray(supplierIds)) {
          params.append('supplierIds', supplierIds.join(','));
        } else {
          params.append('supplierIds', supplierIds);
        }
      }

      const response = await api.get(`inventory-management/search?${params.toString()}`);
      return response.data.success ? response.data.data : response.data;
    } catch (error) {
      return handleApiError('searchInventoryApi', error, rejectWithValue);
    }
  },
);
